import { Permission, PermissionsAndroid, Platform } from 'react-native';
import { check, PERMISSIONS, request, RESULTS } from 'react-native-permissions';
import messaging from '@react-native-firebase/messaging';
import { Logger } from './Logger';

export class Permissions {
  public static async requestLocationPermission() {
    if (Platform.OS !== 'android') {
      return;
    }
    await this.requestAndroidPermission('android.permission.ACCESS_FINE_LOCATION');
  }

  public static async requestNotificationPermission(): Promise<boolean> {
    if (this.isNotificationPermissionRequiredForAndroid()) {
      try {
        const notificationPermission = 'android.permission.POST_NOTIFICATIONS' as Permission;
        const result = await PermissionsAndroid.request(notificationPermission);
        // TODO: handle never_ask_again
        return result === PermissionsAndroid.RESULTS.GRANTED;
      } catch (error) {
        Logger.error(`Error requesting notification permission (Android >= 33): ${error}`);
        return false;
      }
    } else if (Platform.OS === 'ios') {
      try {
        const authStatus = await messaging().requestPermission();
        const granted = authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
                        authStatus === messaging.AuthorizationStatus.PROVISIONAL;
        return granted;
      } catch (error) {
        Logger.error(`Error requesting notification permission (iOS): ${error}`);
        return false;
      }
    }
    // For Android < 33 or other platforms, assume granted or not needed by default
    return true;
  }

  public static async checkNotificationPermission(): Promise<boolean> {
    if (this.isNotificationPermissionRequiredForAndroid()) {
      try {
        const notificationPermission = 'android.permission.POST_NOTIFICATIONS' as Permission;
        const result = await PermissionsAndroid.check(notificationPermission);
        return result;
      } catch (error) {
        Logger.error(`Error checking notification permission (Android >= 33): ${error}`);
        return false;
      }
    } else if (Platform.OS === 'ios') {
      try {
        const authStatus = await messaging().hasPermission();
        return authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
               authStatus === messaging.AuthorizationStatus.PROVISIONAL;
      } catch (error) {
        Logger.error(`Error checking notification permission (iOS): ${error}`);
        return false;
      }
    }
    // For Android < 33 or other platforms, assume granted or not needed by default
    return true;
  }

  public static async requestCameraPermission() {
    const cameraPlatformToCheck = Platform.OS === 'android' ? PERMISSIONS.ANDROID.CAMERA : PERMISSIONS.IOS.CAMERA;
    const result = await request(cameraPlatformToCheck);
    return result === RESULTS.GRANTED;
  }

  public static async isCameraPermissionGranted(): Promise<boolean>  {
    const cameraPlatformToCheck = Platform.OS === 'android' ? PERMISSIONS.ANDROID.CAMERA : PERMISSIONS.IOS.CAMERA;
    const permissionsStatus = await check(cameraPlatformToCheck);
    return permissionsStatus === RESULTS.GRANTED;
  }

  public static async requestMicrophonePermission() {
    const permission = Platform.OS === 'android' ? PERMISSIONS.ANDROID.RECORD_AUDIO : PERMISSIONS.IOS.MICROPHONE;
    const permissionStatus = await request(permission);
    return permissionStatus === RESULTS.GRANTED;
  }

  public static async isMicrophonePermissionGranted(): Promise<boolean> {
    const permission = Platform.OS === 'android' ? PERMISSIONS.ANDROID.RECORD_AUDIO : PERMISSIONS.IOS.MICROPHONE;
    const permissionStatus = await check(permission);
    return permissionStatus === RESULTS.GRANTED;
  }

  private static isNotificationPermissionRequiredForAndroid(): boolean {
    if (Platform.OS === 'android' && Platform.Version >= 33) {
      return true;
    }
    return false;
  }

  private static requestAndroidPermission = async (
    permission: Permission,
  ) => {
    await PermissionsAndroid.request(
      permission,
    );
  }
}
